import React, { useEffect, useState } from "react";
import axios from "axios";
import { useIntl } from "react-intl";
import Box from "@mui/material/Box";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import { useTheme } from "@mui/styles";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import useNavigateEntity from "hooks/useNavigateEntity";
import { apiUrl } from "features/configure";
import { get } from "lodash-es";
import Button, {
  COLOR,
  VARIANT,
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/Button";
import { StyledStepListItem } from "./TelegramNotificationSetting.styles";
import { useOutletContext } from "react-router-dom";
import { Loading } from "features/common";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";

const TelegramNotificationSetting = () => {
  const theme = useTheme();
  const { navigateEntity } = useNavigateEntity();
  const { entityConfig } = useFetchEntityConfig();
  const [entityName] = useOutletContext();
  const { formatMessage } = useIntl();
  const dispatchSnackbar = useDispatchSnackbar();

  const [imgNo, setImgNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [botLink, setBotLink] = useState("");

  const [isSettingComplete, setIsSettingComplete] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const isConnected = get(
    entityConfig,
    "serviceModule.telegramNotification.connected"
  );

  const imageSteps = {
    1: {
      heading: formatMessage({
        id: "telegramNotification.integrationWizard.instruction1.imgTitle",
      }),
      img: require("assets/img/tg-select-grp-preview.svg").default,
    },
    2: {
      heading: formatMessage({
        id: "telegramNotification.integrationWizard.instruction2.imgTitle",
      }),
      img: require("assets/img/tg-disable-admin-permission.png"),
    },
  };

  useEffect(async () => {
    if (isConnected) {
      fallbackSettingRoute();
    } else {
      if (!botLink) {
        await getTgBotLink();
      }
    }
  }, [isConnected]);

  useEffect(async () => {
    if (isSettingComplete) {
      await checkIntegrationStatus();
    }
  }, [isSettingComplete]);

  const fallbackRoute = () => {
    navigateEntity("/settings/general/integration/");
  };

  const fallbackSettingRoute = () => {
    navigateEntity("/settings/general/integration/update/telegramNotification");
  };

  const getTgBotLink = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        apiUrl + `/api/telegram/notification/${entityName}/getBotLink`
      );
      setBotLink(data.botLink);
    } catch (error) {
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.commom" }),
        variant: "error",
      });
      fallbackRoute();
    } finally {
      setLoading(false);
    }
  };

  const checkIntegrationStatus = async () => {
    try {
      await axios.get(
        apiUrl + `/api/telegram/notification/${entityName}/integrationStatus`
      );
      setIsSuccess(true);
      dispatchSnackbar({ message: "Integration success!", variant: "success" });
    } catch (error) {
      dispatchSnackbar({
        message: formatMessage({ id: "global.snackbar.error.noDetectedGroup" }),
        variant: "error",
      });
      setIsSuccess(false);
      setIsSettingComplete(false);
      navigateEntity("/settings/customization/telegramNotification");
    }
  };

  const handleOpenTelegramBot = () => {
    window.open(botLink, "_blank", "noopener,noreferrer");
  };

  const renderCheckIntegrationScreen = () => {
    if (isSuccess) {
      return (
        <div className="column" style={{ height: "calc(100vh - 60px)" }}>
          <img
            src={require(`assets/img/img_completed.svg`).default}
            style={{ height: "40vh", marginBottom: 25 }}
          />
          <div className="lgTitle">
            {formatMessage({
              id: "telegramNotification.integrationWizard.success.title",
            }) + " 🎉"}
          </div>
          <div className="description">
            {formatMessage({
              id: "telegramNotification.integrationWizard.success.description",
            })}
          </div>
          <div style={{ margin: "32px auto" }}>
            <Button
              variant={VARIANT.OUTLINED}
              color={COLOR.SECONDARY}
              size={SIZE.XL}
              style={{ marginRight: 8, width: 160 }}
              onClick={() => fallbackRoute()}
            >
              {formatMessage({ id: "global.btn.backSetting" })}
            </Button>
            <Button
              color={COLOR.SECONDARY}
              onClick={() => fallbackSettingRoute()}
              size={SIZE.XL}
              style={{ width: 160 }}
            >
              {formatMessage({ id: "global.btn.goCustomize" })}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <Loading
        open={true}
        text={
          <div style={{ textAlign: "center" }}>
            {formatMessage({ id: "integration.checkingStatus.description" })}
            <div>
              <Button
                color={COLOR.SECONDARY}
                style={{ margin: 0, marginTop: "20px" }}
                onClick={() => fallbackRoute()}
              >
                {formatMessage({ id: "global.btn.backToSetting" })}
              </Button>
            </div>
          </div>
        }
      />
    );
  };

  if (isSettingComplete) {
    return renderCheckIntegrationScreen();
  }

  return (
    <>
      <Loading open={loading} fullScreen={false} />
      <Box mb="75px" className="fade-up">
        <div>
          <Box
            sx={{
              color: "#A5A5A5",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 14,
              marginRight: 4,
              margin: "12px 0",
              marginTop: "-30px",
            }}
            onClick={fallbackRoute}
          >
            {formatMessage({ id: "global.btn.back" })}
            <i
              className="meta-crm-icon-ic_redo font-size-14"
              style={{ marginLeft: 4 }}
            />
          </Box>
        </div>
      </Box>
      <Box
        sx={{
          height: "calc(80vh - 60px)",
          display: "flex",
          alignItems: "center",
        }}
        className="fade-up-more"
      >
        <Box component={Grid} container>
          <Box component={Grid} item xs={12} sm={6} mt={"42px"}>
            <Box>
              <Box
                fontSize={"24px"}
                fontWeight={"700"}
                color={(theme) => theme.customColors.grey[800]}
                mb="8px"
              >
                {formatMessage({
                  id: "telegramNotification.integrationWizard.title",
                })}
              </Box>
              <Box color={(theme) => theme.customColors.grey[600]} mb="32px">
                {formatMessage({
                  id: "telegramNotification.integrationWizard.description",
                })}
              </Box>
              <StyledStepListItem>
                {formatMessage({
                  id: "telegramNotification.integrationWizard.instruction1",
                })}
                <Box mt="8px" mb="12px">
                  <Button
                    variant={VARIANT.OUTLINED}
                    size={SIZE.SMALL}
                    onClick={handleOpenTelegramBot}
                  >
                    {formatMessage({ id: "telegramNotification.btn.goToBot" })}
                  </Button>
                </Box>
              </StyledStepListItem>
              <StyledStepListItem>
                {formatMessage({
                  id: "telegramNotification.integrationWizard.instruction2",
                })}{" "}
                <span
                  onClick={() => setImgNo(1)}
                  style={{
                    color: theme.customColors.purple[500],
                    fontWeight: theme.fontWeight.BOLD,
                    cursor: "pointer",
                    borderBottom: `1px solid ${theme.customColors.purple[500]}`,
                  }}
                >
                  {"( img 01 )"}
                </span>
              </StyledStepListItem>
              <StyledStepListItem>
                {formatMessage({
                  id: "telegramNotification.integrationWizard.instruction3",
                })}{" "}
                <span
                  onClick={() => setImgNo(2)}
                  style={{
                    color: theme.customColors.purple[500],
                    fontWeight: theme.fontWeight.BOLD,
                    cursor: "pointer",
                    borderBottom: `1px solid ${theme.customColors.purple[500]}`,
                  }}
                >
                  {"( img 02 )"}
                </span>
              </StyledStepListItem>
              <StyledStepListItem>
                {formatMessage({
                  id: "telegramNotification.integrationWizard.instruction4",
                })}
              </StyledStepListItem>
              <StyledStepListItem>
                {formatMessage({
                  id: "telegramNotification.integrationWizard.instruction5",
                })}
              </StyledStepListItem>
            </Box>
            <Box mt="50px">
              <Button
                variant={VARIANT.OUTLINED}
                sx={{ width: "120px", marginRight: "8px" }}
                onClick={fallbackRoute}
              >
                {formatMessage({ id: "global.btn.back" })}
              </Button>
              <Button
                sx={{ width: "120px" }}
                onClick={() => {
                  setIsSettingComplete(true);
                }}
              >
                {formatMessage({ id: "global.btn.complete" })}
              </Button>
            </Box>
          </Box>
          <Box component={Grid} item xs={12} sm={6}>
            <Box
              sx={{
                backgroundColor: "#e6e6e63d",
                padding: "17px 13px 0px",
              }}
            >
              <Box
                sx={{
                  fontWeight: "700",
                  color: theme.customColors.grey[700],
                  marginBottom: "32px",
                }}
              >
                <Box
                  sx={{
                    color: theme.customColors.purple[500],
                    display: "inline-block",
                  }}
                >
                  {" "}
                  img 0{imgNo}.
                </Box>{" "}
                {imageSteps[imgNo].heading}
              </Box>
              <Box
                sx={{ margin: "0 auto", display: "block" }}
                component={"img"}
                src={imageSteps[imgNo].img}
                width={"279px"}
                height={"438px"}
              ></Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TelegramNotificationSetting;
